import { useMemo } from "react";
import _ from "lodash";

import Box from "components/Box";
import NoPrint from "components/NoPrint";
import Stack from "components/Stack";

import MemoSectionContextButton from "entities/Memo/components/Sections/ContextButton";
import NASection from "entities/Memo/components/Sections/NASection";
import NoProformaUploaded from "entities/Memo/components/Sections/NoProformaUploaded";
import SectionSummary from "entities/Memo/components/Sections/Summary";
import { IMemoSection } from "entities/Memo/sdk";
import { useProformaForDealManager } from "entities/Memo/utils";
import { EMemoSourcesAndUsesSectionDisplayMode } from "entities/MemoTemplate/constants";
import SourcesAndUsesConstruction from "entities/Proforma/components/ProformaSourcesAndUses/SourcesAndUsesConstruction";
import SourcesAndUsesPermanent from "entities/Proforma/components/ProformaSourcesAndUses/SourcesAndUsesPermanent";

interface ISectionSourcesAndUses {
  dealId: number;
  externalToken?: string;
  section?: IMemoSection;
  onUpdate?: () => void;
}

const SectionSourcesAndUses = ({
  dealId,
  externalToken,
  section,
  onUpdate,
}: ISectionSourcesAndUses) => {
  const { data: proforma } = useProformaForDealManager({
    token: externalToken,
    dealId,
  });

  const isSourcesAndUsesConstruction = useMemo(
    () =>
      !_.isNil(proforma?.core_data?.sources_at_construction_data) &&
      !_.isNil(proforma?.core_data?.uses_at_construction_data),
    [
      proforma?.core_data?.sources_at_construction_data,
      proforma?.core_data?.uses_at_construction_data,
    ]
  );

  const isSourcesAndUsesPermanent = useMemo(
    () =>
      !_.isNil(proforma?.core_data?.sources_permanent_data) &&
      !_.isNil(proforma?.core_data?.uses_permanent_data),
    [
      proforma?.core_data?.sources_permanent_data,
      proforma?.core_data?.uses_permanent_data,
    ]
  );

  const hasUploadedProforma = useMemo(
    () => isSourcesAndUsesConstruction || isSourcesAndUsesPermanent,
    [isSourcesAndUsesConstruction, isSourcesAndUsesPermanent]
  );
  return (
    <>
      {!_.isNil(proforma) &&
      !_.isNil(proforma?.core_data) &&
      hasUploadedProforma ? (
        <Stack spacing={2} data-testid="sources-and-uses-section">
          {section && _.isUndefined(externalToken) && (
            <NoPrint
              sx={{
                alignSelf: "flex-end",
              }}
            >
              <MemoSectionContextButton
                dealId={dealId}
                section={section}
                onUpdate={onUpdate}
              />
            </NoPrint>
          )}
          {section?.is_na ? (
            <NASection />
          ) : (
            <Box>
              {!_.isNil(section) &&
                [
                  EMemoSourcesAndUsesSectionDisplayMode.MIXED,
                  EMemoSourcesAndUsesSectionDisplayMode.ONLY_CONSTRUCTION,
                ].includes(section.sources_and_uses_section_display_mode) &&
                isSourcesAndUsesConstruction && (
                  <Box sx={{ marginBottom: 3 }}>
                    <SourcesAndUsesConstruction coreData={proforma.core_data} />
                  </Box>
                )}
              {!_.isNil(section) &&
                [
                  EMemoSourcesAndUsesSectionDisplayMode.MIXED,
                  EMemoSourcesAndUsesSectionDisplayMode.ONLY_PERMANENT,
                ].includes(section.sources_and_uses_section_display_mode) &&
                isSourcesAndUsesPermanent && (
                  <Box sx={{ marginBottom: 3 }}>
                    <SourcesAndUsesPermanent coreData={proforma?.core_data} />
                  </Box>
                )}
              <Box>
                <SectionSummary
                  title="Sources and uses description"
                  category="sources and uses"
                  dealId={dealId}
                  externalToken={externalToken}
                />
              </Box>
            </Box>
          )}
        </Stack>
      ) : (
        <NoProformaUploaded title="Sources and Uses" />
      )}
    </>
  );
};

export default SectionSourcesAndUses;
